import React, { useEffect, useState } from 'react';
import Logo from './Logo';
import DocumentPreviewModal from './DocumentPreviewModal';
import DeviceActivation from './DeviceActivation';
import LanguageSwitcher from './LanguageSwitcher';
import { api } from '../api/client';
import { useLanguage } from '../i18n/LanguageContext';
import '../styles/kiosk.css';

const emptyForm = {
  nome: '', cognome: '', email: '', telefono: '', azienda: '', ruolo: '', note: '',
  privacy_accepted: false,
};

export default function ContactsKiosk() {
  const { language, t } = useLanguage();
  const [licenseValid, setLicenseValid] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [deviceAuthorized, setDeviceAuthorized] = useState(null);
  const [eventName, setEventName] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(false);

  const checkDeviceStatus = () => {
    api.getDeviceStatus()
      .then((res) => setDeviceAuthorized(!res.authRequired || res.authorized))
      .catch(() => setDeviceAuthorized(true)); // in caso di errore non blocca la pagina
  };

  useEffect(() => {
    api.getLicenseStatus()
      .then((res) => {
        setLicenseValid(res.valid);
        const enabled = res.valid && Array.isArray(res.features) && res.features.includes('fiere');
        setFeatureEnabled(enabled);
        if (enabled) {
          api.getEventContactConfigPublic()
            .then((cfgRes) => setEventName(cfgRes?.data?.evento || ''))
            .catch(() => setEventName(''));
        }
      })
      .catch(() => {
        setLicenseValid(false);
        setFeatureEnabled(false);
      });

    checkDeviceStatus();
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.nome.trim() || !form.cognome.trim() || !form.email.trim()) {
      setError(t('contactsErrorRequired'));
      return;
    }
    if (!form.privacy_accepted) {
      setError(t('contactsErrorPrivacy'));
      return;
    }

    setSubmitting(true);
    try {
      await api.submitEventContact({ ...form, language });
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewContact = () => {
    setForm(emptyForm);
    setSuccess(false);
    setError('');
  };

  if (licenseValid === null || deviceAuthorized === null) return <div className="kiosk" />;

  if (!licenseValid) {
    return (
      <div className="kiosk">
        <div className="kiosk-header">
          <div className="logo-frame"><Logo size={44} /></div>
        </div>
        <p style={{ color: 'var(--muted-text)', maxWidth: 360, textAlign: 'center' }}>
          Il servizio non è ancora attivo su questo dispositivo. Contatta l'amministratore.
        </p>
      </div>
    );
  }

  if (!deviceAuthorized) {
    return <DeviceActivation onActivated={checkDeviceStatus} />;
  }

  if (!featureEnabled) {
    return (
      <div className="kiosk">
        <div className="kiosk-header">
          <div className="logo-frame"><Logo size={44} /></div>
        </div>
        <p style={{ color: 'var(--muted-text)', maxWidth: 360, textAlign: 'center' }}>
          Questo modulo non è incluso nella licenza di questa installazione. Contatta il fornitore per attivarlo.
        </p>
      </div>
    );
  }

  return (
    <div className="kiosk">
      <LanguageSwitcher />

      <div className="kiosk-header">
        <div className="logo-frame"><Logo size={44} /></div>
        <span className="kiosk-subtitle">{eventName || t('contactsSubtitle')}</span>
      </div>

      <div className="contacts-form-wrap">
        {success ? (
          <div className="form-success">
            <div className="success-icon">✓</div>
            <h2>{t('contactsSuccessTitle')}</h2>
            <p className="modal-subtitle">{t('contactsSuccessSubtitle')}</p>
            <button className="btn btn-primary" onClick={handleNewContact}>{t('contactsNewButton')}</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ marginBottom: 4 }}>{t('contactsFormTitle')}</h2>
            <p className="modal-subtitle">{t('contactsFormSubtitle')}</p>

            {error && <div className="form-error">{error}</div>}

            <div className="badge-card">
              <div className="field-row">
                <div className="field">
                  <label htmlFor="c-nome">{t('fieldNome')}</label>
                  <input id="c-nome" type="text" value={form.nome} onChange={update('nome')} autoComplete="given-name" />
                </div>
                <div className="field">
                  <label htmlFor="c-cognome">{t('fieldCognome')}</label>
                  <input id="c-cognome" type="text" value={form.cognome} onChange={update('cognome')} autoComplete="family-name" />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-email">{t('fieldEmail')}</label>
                <input id="c-email" type="email" value={form.email} onChange={update('email')} autoComplete="email" />
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="c-telefono">{t('fieldTelefono')} <span className="optional-tag">{t('optional')}</span></label>
                  <input id="c-telefono" type="tel" value={form.telefono} onChange={update('telefono')} autoComplete="tel" />
                </div>
                <div className="field">
                  <label htmlFor="c-azienda">{t('fieldDitta')} <span className="optional-tag">{t('optional')}</span></label>
                  <input id="c-azienda" type="text" value={form.azienda} onChange={update('azienda')} autoComplete="organization" />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-ruolo">{t('fieldRuolo')} <span className="optional-tag">{t('optional')}</span></label>
                <input id="c-ruolo" type="text" value={form.ruolo} onChange={update('ruolo')} autoComplete="organization-title" />
              </div>

              <div className="field">
                <label htmlFor="c-note">{t('fieldNote')} <span className="optional-tag">{t('optional')}</span></label>
                <textarea id="c-note" rows={2} maxLength={1000} value={form.note} onChange={update('note')} />
              </div>
            </div>

            <div className="register-consents" style={{ gridTemplateColumns: '1fr' }}>
              <button
                type="button"
                className={`consent-card ${form.privacy_accepted ? 'consent-card--accepted' : ''}`}
                onClick={() => setPreviewDoc(true)}
              >
                <span className="consent-card__icon" aria-hidden="true">
                  {form.privacy_accepted ? (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M7 3h7l4 4v14H7V3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>
                  )}
                </span>
                <span className="consent-card__text">
                  <span className="consent-card__label">{t('linkPrivacy')}</span>
                  <span className="consent-card__status">{form.privacy_accepted ? t('consentDone') : t('consentToRead')}</span>
                </span>
              </button>
            </div>

            <div className="btn-row" style={{ marginTop: 20 }}>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? t('submitting') : t('contactsSubmitButton')}
              </button>
            </div>
          </form>
        )}
      </div>

      <span className="kiosk-version" />

      {previewDoc && (
        <DocumentPreviewModal
          docType="event_privacy"
          onClose={() => setPreviewDoc(false)}
          requireAcceptance
          onAccept={() => setForm((f) => ({ ...f, privacy_accepted: true }))}
        />
      )}
    </div>
  );
}
